// import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface TableCheckResult {
  roomConflicts: number
  teacherDoubleBooking: number
  classDoubleBooking: number
}

interface TableCheckModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  result: TableCheckResult | null
  isLoading: boolean
}

export function TableCheckModal({
  open,
  onOpenChange,
  result,
  isLoading,
}: TableCheckModalProps) {
  const { t } = useTranslation('timetable', { keyPrefix: 'timetable-folder.tableCheckModal' })
  const totalErrors = result
    ? result.roomConflicts + result.teacherDoubleBooking + result.classDoubleBooking
    : 0

  const hasErrors = totalErrors > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md w-[95vw] md:w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {t('title')}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-sm text-muted-foreground">{t('checking')}</p>
            </div>
          </div>
        ) : result ? (
          <div className="space-y-4">
            {/* Summary Card */}
            <div className={`p-4 rounded-lg border ${
              hasErrors 
                ? 'bg-red-50 border-red-200' 
                : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {hasErrors ? (
                  <XCircle className="h-5 w-5 text-red-600" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                )}
                <span className={`font-semibold ${
                  hasErrors ? 'text-red-800' : 'text-green-800'
                }`}>
                  {hasErrors ? t('issuesFound') : t('noIssuesFound')}
                </span>
              </div>
              <p className={`text-sm ${
                hasErrors ? 'text-red-700' : 'text-green-700'
              }`}>
                {hasErrors 
                  ? t('conflictsDetected', { count: totalErrors, plural: totalErrors > 1 ? 's' : '' })
                  : t('noConflicts')
                }
              </p>
            </div>

            {/* Error Details */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700">{t('errorSummary')}</h4>
              
              <div className="space-y-2">
                {/* Room Conflicts */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      result.roomConflicts > 0 ? 'bg-red-500' : 'bg-green-500'
                    }`}></div>
                    <span className="text-sm font-medium">{t('roomConflicts')}</span>
                  </div>
                  <Badge variant={result.roomConflicts > 0 ? "destructive" : "secondary"}>
                    {result.roomConflicts}
                  </Badge>
                </div>

                {/* Teacher Double Booking */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      result.teacherDoubleBooking > 0 ? 'bg-red-500' : 'bg-green-500'
                    }`}></div>
                    <span className="text-sm font-medium">{t('teacherDoubleBooking')}</span>
                  </div>
                  <Badge variant={result.teacherDoubleBooking > 0 ? "destructive" : "secondary"}>
                    {result.teacherDoubleBooking}
                  </Badge>
                </div>

                {/* Class Double Booking */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      result.classDoubleBooking > 0 ? 'bg-red-500' : 'bg-green-500'
                    }`}></div>
                    <span className="text-sm font-medium">{t('classDoubleBooking')}</span>
                  </div>
                  <Badge variant={result.classDoubleBooking > 0 ? "destructive" : "secondary"}>
                    {result.classDoubleBooking}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Recommendations */}
            {hasErrors && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h5 className="font-medium text-sm text-blue-800 mb-1">
                  {t('recommendations')}
                </h5>
                <ul className="text-xs text-blue-700 space-y-1">
                  {result.roomConflicts > 0 && (
                    <li>{t('recommendRoom')}</li>
                  )}
                  {result.teacherDoubleBooking > 0 && (
                    <li>{t('recommendTeacher')}</li>
                  )}
                  {result.classDoubleBooking > 0 && (
                    <li>{t('recommendClass')}</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">{t('noResults')}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
} 